import { useState } from "react";
import {
  Box,
  Button,
  Card,
  CardBody,
  Container,
  FormControl,
  HStack,
  Image,
  Input,
  Text,
  VStack,
  useToast,
  Checkbox,
  Textarea,
} from "@chakra-ui/react";
import logo from "../../../assets/images/logo2.png";
import {
  useSubmitUserInterest,
  UserInterestPayload,
  WorkTypes,
  AvailabilityPreferences,
  UserInterestResponse,
  ApiError,
} from "../../../api/UserInterestApi.ts";
import { AxiosError } from "axios";

import RvuPerMonth from "./RvuPerMonth.tsx";
import StateMedicalLicensure from "./StateMedicalLicensure.tsx";
import EngagmentWithPlatform from "./EngagmentWithPlatform.tsx";
import WorkType from "./WorkType.tsx";
import MalpracticeInsurance from "./MalpracticeInsurance.tsx";

const initialFormData: UserInterestPayload = {
  name: "",
  phone: "",
  email: "",
  specialty: "",
  additionalInfo: "",
  availabilityPreferences: {
    days: [],
    hours: [],
  },
  workTypes: {
    weekdays: false,
    weekends: false,
    swingShifts: false,
    overnights: false,
    dedicatedShifts: false,
  },
  rvuPerMonth: "",
  stateLicenses: [],
  agreeToTerms: false,
  hasMalpracticeInsurance: null,
};

export const UserInterest = (): JSX.Element => {
  const [formData, setFormData] = useState<UserInterestPayload>(initialFormData);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const { mutate: submitUserInterest, isPending } = useSubmitUserInterest();
  const toast = useToast();

  const handleInputChange = (
    field: keyof UserInterestPayload | `${keyof UserInterestPayload}.${string}`,
    value: string | boolean | string[] | null,
  ): void => {
    setFormData((prev) => {
      if (typeof field === "string" && field.includes(".")) {
        const [parent, child] = field.split(".") as [keyof UserInterestPayload, string];
        if (parent === "availabilityPreferences" || parent === "workTypes") {
          return {
            ...prev,
            [parent]: {
              ...prev[parent],
              [child]: value,
            },
          };
        }
      }
      return {
        ...prev,
        [field]: value,
      };
    });
  };

  const isFormValid = (): boolean => {
    return (
      !!formData.name &&
      !!formData.email &&
      formData.agreeToTerms &&
      formData.stateLicenses.length > 0 &&
      formData.hasMalpracticeInsurance !== null
    );
  };

  const handleSubmit = (): void => {
    if (!isFormValid()) {
      toast({
        title: "Error",
        description: "Please fill in all required fields and agree to the terms",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    submitUserInterest(formData, {
      onSuccess: (response: UserInterestResponse) => {
        toast({
          title: "Success",
          description: response.message,
          status: "success",
          duration: 5000,
          isClosable: true,
        });
        setFormData(initialFormData);
        setIsSubmitted(true);
      },
      onError: (error: AxiosError<ApiError>) => {
        toast({
          title: "Error",
          description: error.response?.data?.message ?? "An error occurred",
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      },
    });
  };

  const handleMultiSelect = (field: keyof AvailabilityPreferences, value: string): void => {
    const currentValues = formData.availabilityPreferences[field];
    const newValues = currentValues.includes(value)
      ? currentValues.filter((v) => v !== value)
      : [...currentValues, value];
    handleInputChange(`availabilityPreferences.${field}`, newValues);
  };

  const handleWorkTypeChange = (key: keyof WorkTypes): void => {
    handleInputChange(`workTypes.${key}`, !formData.workTypes[key]);
  };

  return (
    <Box
      minHeight="100vh"
      bg="#2E3192"
      display="flex"
      flexDirection="column"
      alignItems="center"
      justifyContent="center"
      py={8}
      overflow="hidden"
    >
      <Container maxW="container.md" py={8}>
        <VStack spacing={8}>
          <Image src={logo} alt="LnQ" w="160px" />

          {isSubmitted ? (
            <Card w="488" borderRadius="xl" bg="white">
              <CardBody p={14}>
                <VStack spacing={4} align="center">
                  <Text
                    fontSize="24px"
                    fontWeight="700"
                    color="brandBlue.800"
                    textAlign="center"
                  >
                    Thank you for your interest!
                  </Text>
                  <Text fontSize="16px" color="gray.600" textAlign="center">
                    A LnQ team member will be contacting you soon.
                  </Text>
                </VStack>
              </CardBody>
            </Card>
          ) : (
            <Card w="488" borderRadius="xl">
              <CardBody p={14}>
                <VStack spacing={6} align="stretch">
                  <VStack spacing={2} align="center" mb={4}>
                    <Text
                      fontSize="24px"
                      fontWeight="700"
                      fontFamily="inter"
                      color="brandBlue.800"
                      textAlign="center"
                      maxW="315"
                    >
                      Thanks for your interest in LnQ!
                    </Text>
                    <Text
                      fontSize="16px"
                      color="gray.600"
                      textAlign="center"
                      fontFamily="inter"
                      maxW="315"
                    >
                      Please fill in all fields below.
                    </Text>
                  </VStack>
                  <VStack spacing={4} width="100%" align="center">
                    <FormControl isRequired width="100%" maxWidth="368px">
                      <Input
                        placeholder="Name"
                        value={formData.name}
                        height={14}
                        onChange={(e) => handleInputChange("name", e.target.value)}
                      />
                    </FormControl>

                    <FormControl isRequired width="100%" maxWidth="368px">
                      <Input
                        placeholder="Phone Number"
                        value={formData.phone}
                        height={14}
                        onChange={(e) => handleInputChange("phone", e.target.value)}
                      />
                    </FormControl>

                    <FormControl isRequired width="100%" maxWidth="368px">
                      <Input
                        placeholder="Email"
                        type="email"
                        value={formData.email}
                        height={14}
                        onChange={(e) => handleInputChange("email", e.target.value)}
                      />
                    </FormControl>

                    <FormControl width="100%" maxWidth="368px">
                      <Input
                        placeholder="Sub Specialty interests"
                        value={formData.specialty}
                        height={14}
                        onChange={(e) => handleInputChange("specialty", e.target.value)}
                      />
                    </FormControl>
                    <FormControl width="100%" maxWidth="368px">
                      <Text color="gray.500" fontSize="12px" fontWeight="700" mb={4}>
                        We assume physicians are open to ad-hoc/non scheduled work at their
                        affiliated facilities. If you prefer only scheduled work, please
                        indicate this in the free text below.
                      </Text>
                      <Textarea
                        placeholder="Feel free to share any additional information about yourself."
                        value={formData.additionalInfo}
                        onChange={(e) =>
                          handleInputChange("additionalInfo", e.target.value)
                        }
                        maxLength={500}
                        height="120px"
                        resize="none"
                      />
                    </FormControl>
                  </VStack>

                  <EngagmentWithPlatform
                    formData={formData}
                    onDaysChange={(val) => handleMultiSelect("days", val)}
                    onHoursChange={(val) => handleMultiSelect("hours", val)}
                  />

                  <WorkType formData={formData} onWorkTypeChange={handleWorkTypeChange} />
                  <RvuPerMonth
                    formData={formData}
                    onRvuPerMonth={(val) => handleInputChange("rvuPerMonth", val)}
                  />
                  <StateMedicalLicensure
                    formData={formData}
                    onStateLicenseChange={(val) => handleInputChange("stateLicenses", val)}
                  />
                  <MalpracticeInsurance
                    formData={formData}
                    onMalpracticeChange={(val) =>
                      handleInputChange("hasMalpracticeInsurance", val)
                    }
                  />
                  <HStack spacing={2} align="flex-start" maxW="368">
                    <Checkbox
                      colorScheme="brandBlue"
                      isChecked={formData.agreeToTerms}
                      onChange={(e) => handleInputChange("agreeToTerms", e.target.checked)}
                      size="lg"
                    />
                    <Text color="gray.600" fontSize="12px">
                      I agree to receive emails and SMS messages and understand I can opt
                      out anytime.
                    </Text>
                  </HStack>
                  <Text
                    color="gray.500"
                    fontSize="12px"
                    fontWeight="700"
                    mb={4}
                    width="360px"
                  >
                    After you have submitted, be on the lookout for someone from LnQ to
                    reach out to you to schedule a follow up conversation focused on how we
                    can best find work that matches your preferences.
                  </Text>
                  <Button
                    colorScheme={isFormValid() ? "brandYellow" : "gray"}
                    size="lg"
                    w="full"
                    color={isFormValid() ? "brandBlue.800" : "gray.500"}
                    fontWeight="bold"
                    isLoading={isPending}
                    isDisabled={!isFormValid()}
                    onClick={() => handleSubmit()}
                    _disabled={{
                      bg: "#F1EFEE",
                      color: "#2E3192",
                      fontsize: "16px",
                      opacity: 0.5,
                      cursor: "not-allowed",
                    }}
                  >
                    Submit
                  </Button>
                </VStack>
              </CardBody>
            </Card>
          )}
        </VStack>
      </Container>
    </Box>
  );
};
